import type { ExportColumn } from '@/components/ui'
import { isCustomerRow } from '../components/PricingRowHelpers'
import type { LookupDirection, PricingRow } from '../api/types'

/**
 * Export column list for Product Customer Pricing. Mirrors the visible
 * `columns` memo in `ProductCustomerPricingPage.tsx` (identity column,
 * then the shared BasePricingRow fields), minus the checkbox/sparkline/
 * tooltip-only columns that have no plain-value export equivalent.
 *
 * Direction-dependent — `by-product` rows are customers (with booked
 * $/% that reverse mode never returns); `by-customer` rows are products.
 * Stable keys: NEVER rename — user templates in localStorage reference
 * them by key.
 */
export function pricingExportColumns(
  direction: LookupDirection,
  unit: 'kg' | 'lb',
): readonly ExportColumn<PricingRow>[] {
  const priceHeader = unit === 'kg' ? 'Price / kg' : 'Price / lb'
  const costHeader = unit === 'kg' ? 'Cost / kg' : 'Cost / lb'

  const cols: ExportColumn<PricingRow>[] = [
    {
      key: 'identity',
      header: direction === 'by-product' ? 'Customer' : 'Product',
      value: (r) => (isCustomerRow(r) ? r.customer_name : r.product_name),
    },
    {
      key: 'default_code',
      header: 'Product code',
      value: (r) => (isCustomerRow(r) ? '' : r.default_code),
    },
    { key: 'last_order_date', header: 'Last order', value: (r) => r.last_order_date },
    { key: 'qty_last', header: 'Qty (last order)', value: (r) => r.qty_last },
    { key: 'price_per_unit', header: priceHeader, value: (r) => (unit === 'kg' ? r.price_per_kg : r.price_per_lb) },
    {
      key: 'cost_per_unit',
      header: costHeader,
      value: (r) => (unit === 'kg' ? r.cost_per_kg_last : r.cost_per_lb_last),
    },
    { key: 'true_margin_pct_last', header: 'Margin % (last order)', value: (r) => r.true_margin_pct_last },
    { key: 'has_dedicated_pricelist', header: 'Dedicated pricelist', value: (r) => (r.has_dedicated_pricelist ? 'Yes' : 'No') },
    { key: 'below_margin_floor', header: 'Margin floor', value: (r) => (r.below_margin_floor ? 'Below floor' : 'At/above floor') },
  ]

  if (direction === 'by-product') {
    cols.push(
      { key: 'ordered_revenue', header: 'Booked $', value: (r) => (isCustomerRow(r) ? r.ordered_revenue : '') },
      { key: 'ordered_margin_pct', header: 'Booked %', value: (r) => (isCustomerRow(r) ? r.ordered_margin_pct : '') },
    )
  }

  cols.push(
    { key: 'orders_count_in_window', header: 'Orders', value: (r) => r.orders_count_in_window },
    { key: 'total_revenue_in_window', header: 'Revenue', value: (r) => r.total_revenue_in_window },
  )

  return cols
}
